import React, { useEffect, useState, createContext, useContext } from "react";
import { X } from "lucide-react";
import { Button } from "./ui/button";
import { motion, AnimatePresence } from "framer-motion";

// Context for close condition
const CloseConditionContext = createContext<{
    setCanClose: (fn: () => boolean | Promise<boolean>) => void;
}>({
    setCanClose: () => {},
});

export const useCloseCondition = () => useContext(CloseConditionContext);

interface FullScreenOverlayProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    headerActions?: React.ReactNode;
}

export function FullScreenModal({
    isOpen,
    onClose,
    title,
    children,
    headerActions,
}: FullScreenOverlayProps) {
    const [isClosing, setIsClosing] = useState(false);
    const [canCloseFn, setCanCloseFn] = useState<
        () => boolean | Promise<boolean>
    >(() => () => true);

    const handleClose = async () => {
        const canClose = await canCloseFn();
        if (!canClose) return;

        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
        }, 400);
    };

    // Handle close with animation
    // const handleClose = () => {
    //     setIsClosing(true);
    //     // Wait for animation to complete before calling onClose
    //     setTimeout(() => {
    //         onClose();
    //         setIsClosing(false);
    //     }, 400); // Match this with animation duration
    // };

    // Prevent body scrolling when overlay is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    return (
        <CloseConditionContext.Provider
            value={{
                setCanClose: (fn) => setCanCloseFn(() => fn),
            }}
        >
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="fixed inset-0 z-50 flex flex-col bg-md-surface lg:bg-md-surface/90 lg:backdrop-blur-sm max-w-screen max-h-screen overflow-hidden shadow-lg"
                        initial={{ opacity: 0, y: "100%" }}
                        animate={isClosing ? "closed" : "open"}
                        exit={{ opacity: 0, y: "100%" }}
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 30,
                            duration: 0.4,
                        }}
                        variants={{
                            open: { opacity: 1, y: 0 },
                            closed: { opacity: 0, y: "100%" },
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-3 py-1 bg-md-surface -bg-md-surface-level-1  h-12 -shadow-sm z-10">
                            {/* <div className="w-10"></div> */}
                            <div className="flex items-center justify-center md:pl-10 md:flex-1">
                                <h2 className="text-base font-medium text-md-on-surface">
                                    {title}
                                </h2>
                            </div>

                            <div className="flex items-center gap-2">
                                {headerActions}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleClose}
                                    disabled={isClosing}
                                    className="size-10"
                                >
                                    <X className="size-5" />
                                </Button>
                            </div>
                        </div>

                        {/* Content - scrollable */}
                        <div className="flex-1 overflow-x-hidden overflow-y-auto  bg-md-background">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </CloseConditionContext.Provider>
    );
}
